import { useContext, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'
import { Star } from 'lucide-react'
import { FAVORITES_EVENT, saveFavorites } from '../../services/favorites'
import { toList } from '../../utils/collections'

/**
 * L'étoile « mettre cette page en favori » — la même partout : une fois dans
 * l'en-tête (elle vise alors la PAGE COURANTE, donc elle est sur tous les
 * écrans), et par ligne dans une liste (elle vise l'adresse qu'on lui donne).
 *
 * Elle écrit dans le magasin de signets de la page (`dataLocation` →
 * `{favorites: [url…], favorite_labels: {url: nom}}`, cf. userFavoritesProcessor),
 * enregistre (services/favorites.js — qui explique pourquoi l'enregistrement ne
 * peut pas rester un `submitData`), puis ÉMET `favoriteschanged` UNE FOIS LA
 * RÉPONSE REÇUE : le YAML n'a plus qu'à relire le menu, déclarativement.
 *
 * Pourquoi du React ici : rien en YAML ne connaît la route courante d'une SPA
 * ni le titre affiché à l'écran, et rien ne sait écrire une clé DYNAMIQUE (le
 * libellé est rangé à l'URL du favori) — un `setData` a besoin d'un chemin écrit
 * en dur. Le reste (enregistrer, refetcher le menu) est déjà exprimable en YAML,
 * et y reste.
 *
 * Le libellé enregistré est, dans l'ordre : la prop `label`, le titre affiché
 * (premier `<h1>` de la page), `document.title`, à défaut le dernier segment de
 * l'URL. C'est ce que l'utilisateur vient de lire : aucune page n'a à déclarer
 * quoi que ce soit pour être « étoilable ».
 *
 * Ce libellé NOMME AUSSI le bouton (« Ajouter « X » aux favoris ») : une ligne
 * peut porter plusieurs étoiles — le tableau d'un projet et son dashboard sont
 * deux adresses — et deux boutons de même nom accessible seraient impossibles à
 * distinguer au clavier comme au lecteur d'écran.
 *
 * YAML :
 *   - type: FavoriteToggle
 *     dataLocation: ~~.favs           # { favorites, favorite_labels }
 *     url: ~.page_url                 # facultatif (défaut : la page courante)
 *     label: ~.data.name              # facultatif (défaut : le titre à l'écran)
 *     variant: header                 # header | inline
 *     placement: title                # facultatif : devant le titre de la page
 *     actions:                        # facultatif : relire le menu après coup
 *       - { what: fetchData, on: favoriteschanged, url: /api/orbit/navigation?menu=main, … }
 */
const pageLabel = () => {
  const heading = document.querySelector('main h1, h1')?.textContent?.trim()
  if (heading) return heading
  const title = document.title?.trim()
  if (title && title !== 'Orbit') return title
  const last = window.location.pathname.split('/').filter(Boolean).pop() || ''
  return last ? last.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase()) : 'Page'
}

/**
 * `placement: title` — l'étoile du bandeau se range DEVANT LE TITRE de la page
 * (premier `<h1>` hors bandeau, navigation et modale : beaucoup d’écrans
 * n’ont pas de `<main>`), sur tous les écrans qui ont le menu, sans
 * qu'aucune page n'ait à la déclarer : aucun YAML ne sait viser « le titre de
 * la page courante », qu'il vienne d'un YAML ou d'un écran React.
 *
 * L'hôte est un `<span>` posé en premier enfant du `<h1>` (en ligne avec le
 * texte, quelle que soit la mise en page autour). Le titre est rendu par
 * React : un titre en texte seul qui change est réécrit d'un bloc
 * (`textContent`), ce qui détache l'hôte — d'où l'observateur, qui le repose.
 * Pas de titre (écran plein cadre, chargement) : l'étoile reste dans le bandeau.
 */
const findTitle = () => [...document.querySelectorAll('h1')]
  // Le canevas du builder est du CONTENU édité (h1 éditable), pas le titre de l'écran.
  .find((h) => !h.closest('.orbit-shell-header, nav, dialog, [role="dialog"], .sr-only, #builder-canvas, [contenteditable="true"]')
    && h.getClientRects().length > 0)

const useTitleHost = (enabled) => {
  const [host, setHost] = useState(null)
  useEffect(() => {
    if (!enabled) return undefined
    const el = document.createElement('span')
    el.className = 'inline-flex align-middle -ml-1 mr-1 -my-1 font-normal'
    let frame = 0
    const place = () => {
      frame = 0
      const title = findTitle()
      if (title && title.firstChild !== el) title.insertBefore(el, title.firstChild)
      if (!title && el.isConnected) el.remove()
      setHost(title ? el : null)
    }
    const schedule = () => { if (!frame) frame = requestAnimationFrame(place) }
    const observer = new MutationObserver(schedule)
    observer.observe(document.body, { childList: true, subtree: true })
    place()
    return () => {
      observer.disconnect()
      if (frame) cancelAnimationFrame(frame)
      el.remove()
    }
  }, [enabled])
  return host
}

export const FavoriteToggle = ({ props = {}, path, datafield }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const containerRef = useRef(null)
  const { pathname } = useLocation()
  const resolve = (v) => {
    try {
      return evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })
    } catch {
      return undefined
    }
  }

  const { formData, formDataPath } = propsDataLocationToPathAndValue({
    currentPath: path,
    datafield,
    dataLocation: props.dataLocation,
    defaultValue: {},
    globalDataContext,
    templateContext,
  })
  // `toList` : sous un `DataFilter`, `favorites` arrive en objet indexé — sans
  // quoi l'étoile s'afficherait éteinte malgré des signets enregistrés.
  const favorites = toList(formData?.favorites)
  const labels = formData?.favorite_labels && typeof formData.favorite_labels === 'object'
    ? formData.favorite_labels
    : {}

  const url = String(resolve(props.url) || pathname || '')
  // Le libellé déclaré sert deux fois : nom du signet, et nom du bouton.
  const subject = String(resolve(props.label) || '')
  const titleHost = useTitleHost(resolve(props.placement) === 'title')
  // Devant le titre, l'étoile est sur le fond clair de la page : style « inline ».
  const header = resolve(props.variant) !== 'inline' && !titleHost
  const commitEvent = resolve(props.commitEvent) || FAVORITES_EVENT
  const isFavorite = favorites.includes(url)
  if (!url) return null

  const onClick = () => {
    const label = subject || pageLabel()
    const next = isFavorite
      ? { favorites: favorites.filter((u) => u !== url), favorite_labels: labels }
      : { favorites: [...favorites, url], favorite_labels: { ...labels, [url]: label } }
    // Bascule optimiste : l'étoile change tout de suite, l'écriture suit.
    globalDataContext.updateData(next, formDataPath)
    saveFavorites(next).then((saved) => {
      if (saved) globalDataContext.updateData(saved, formDataPath)
      // Enregistré : le menu peut être relu (le classement vient du back).
      containerRef.current?.dispatchEvent(new Event(commitEvent, { bubbles: true }))
    })
  }

  const title = isFavorite
    ? (subject ? `Retirer « ${subject} » des favoris` : 'Retirer des favoris')
    : (subject ? `Ajouter « ${subject} » aux favoris` : 'Ajouter aux favoris')
  const toggle = (
    <ActionDependant {...props} attributesHolderRef={containerRef}>
      <button
        ref={containerRef}
        type="button"
        onClick={onClick}
        aria-pressed={isFavorite}
        title={title}
        aria-label={title}
        className={header
          ? 'inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors cursor-pointer hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-iris-400 '
            + (isFavorite ? 'text-amber-400' : 'text-[color:var(--text-dark-muted)] hover:text-white')
          : 'inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors cursor-pointer hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-iris-400 '
            // Éteinte, l'étoile reste un CONTRÔLE : slate-500 (4,5:1 sur blanc)
            // au lieu du slate-300 d'origine, sous le seuil de 3:1 du WCAG 1.4.11.
            + (isFavorite ? 'text-amber-400' : 'text-slate-500 hover:text-amber-400')}
      >
        <Star size={header ? 18 : 17} aria-hidden="true" fill={isFavorite ? 'currentColor' : 'none'} />
      </button>
    </ActionDependant>
  )
  return titleHost ? createPortal(toggle, titleHost) : toggle
}

export default FavoriteToggle
